import { apiFetch } from "./api";

export interface LiveCheckpointBoundary {
  checkpoint_id: string;
  context_id: string;
  context_revision: number;
  transcript_last_id: number;
  conversation_messages: number;
  tab_count: number;
  active_url: string | null;
}

/**
 * Codes fermés rendus par `POST /sessions/{id}/live-checkpoint` (tâche 201).
 *
 * `unknown` couvre une réponse d'erreur qui ne suit pas le contrat (proxy,
 * middleware, backend antérieur) : elle est traitée comme transitoire, donc
 * soumise au backoff borné de l'appelant, jamais comme un feu vert.
 */
export type LiveCheckpointErrorCode =
  | "checkpoint_not_ready"
  | "checkpoint_in_progress"
  | "session_not_capturable"
  | "session_stopping"
  | "checkpoint_persist_failed"
  | "unknown";

/** Codes après lesquels aucune capture ne doit plus être tentée. */
const TERMINAL_CODES: ReadonlySet<string> = new Set([
  "session_not_capturable",
  "session_stopping",
]);

const KNOWN_CODES: ReadonlySet<string> = new Set([
  "checkpoint_not_ready",
  "checkpoint_in_progress",
  "session_not_capturable",
  "session_stopping",
  "checkpoint_persist_failed",
]);

/**
 * Échec de capture porteur du contrat serveur.
 *
 * L'ancienne erreur ne portait que le statut : le hook ne pouvait pas
 * distinguer « réessaie dans une seconde » de « cette session est arrêtée »,
 * d'où 1 075 conflits en boucle après un arrêt (fiche 201, P0).
 */
export class LiveCheckpointError extends Error {
  readonly status: number;
  readonly errorCode: LiveCheckpointErrorCode;
  readonly retryable: boolean;
  readonly sessionState: string | null;
  readonly retryAfterMs: number | null;

  constructor(init: {
    status: number;
    errorCode: LiveCheckpointErrorCode;
    retryable: boolean;
    sessionState: string | null;
    retryAfterMs: number | null;
    message: string;
  }) {
    super(init.message);
    this.name = "LiveCheckpointError";
    this.status = init.status;
    this.errorCode = init.errorCode;
    this.retryable = init.retryable;
    this.sessionState = init.sessionState;
    this.retryAfterMs = init.retryAfterMs;
  }

  /** Terminal : le verrou de session doit se poser, pas un simple backoff. */
  get terminal(): boolean {
    return TERMINAL_CODES.has(this.errorCode) || !this.retryable;
  }
}

function asErrorCode(value: unknown): LiveCheckpointErrorCode {
  return typeof value === "string" && KNOWN_CODES.has(value)
    ? (value as LiveCheckpointErrorCode)
    : "unknown";
}

function asRetryAfterMs(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null;
}

async function parseError(response: Response): Promise<LiveCheckpointError> {
  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    // Corps absent ou non JSON (proxy, coupure) : contrat inconnu, ce n'est pas
    // un prétexte à boucler — l'appelant applique son backoff borné.
    body = {};
  }
  const errorCode = asErrorCode(body.error_code);
  const retryable =
    typeof body.retryable === "boolean" ? body.retryable : errorCode === "unknown";
  const sessionState =
    typeof body.session_state === "string" ? body.session_state : null;
  const message =
    typeof body.error === "string" && body.error.length > 0
      ? body.error
      : `capture live checkpoint: ${response.status}`;
  return new LiveCheckpointError({
    status: response.status,
    errorCode,
    retryable,
    sessionState,
    retryAfterMs: asRetryAfterMs(body.retry_after_ms),
    message,
  });
}

/**
 * Capture un checkpoint live.
 *
 * `signal` existe pour que le démontage, un état terminal ou une nouvelle
 * génération puissent annuler la requête EN VOL : sans lui, une réponse tardive
 * réarmait la boucle après l'arrêt de la session.
 */
export async function captureSessionLiveCheckpoint(
  sessionId: string,
  options?: { signal?: AbortSignal },
): Promise<LiveCheckpointBoundary> {
  const response = await apiFetch(`/sessions/${sessionId}/live-checkpoint`, {
    method: "POST",
    ...(options?.signal ? { signal: options.signal } : {}),
  });
  if (!response.ok) {
    throw await parseError(response);
  }
  return (await response.json()) as LiveCheckpointBoundary;
}
